// src/pages/Applications.tsx

import React from "react";
import { supabase } from "../lib/supabaseClient";

type ApplicationStatus =
  | "applied"
  | "interview"
  | "offer"
  | "rejected"
  | "withdrawn";

type ApplicationItem = {
  id?: string;
  title?: string | null;
  company?: string | null;
  location?: string | null;
  url: string;
  applied_at?: string | null;
  status: ApplicationStatus;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  applied: "Applied",
  interview: "Interview",
  offer: "Offer",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

export default function Applications() {
  const API =
    process.env.REACT_APP_API ?? "http://localhost:8000";

  const [applications, setApplications] = React.useState<
    ApplicationItem[]
  >([]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [updatingUrl, setUpdatingUrl] =
    React.useState<string | null>(null);
  const [removingUrl, setRemovingUrl] =
    React.useState<string | null>(null);

  const getToken = React.useCallback(async () => {
    const { data } = await supabase.auth.getSession();

    return data.session?.access_token ?? null;
  }, []);

  const loadApplications = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = await getToken();

      if (!token) {
        setApplications([]);
        setError("Please sign in to view your applications.");
        return;
      }

      const response = await fetch(
        `${API}/api/applications`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not load applications (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      setApplications(
        Array.isArray(data) ? data : []
      );
    } catch (err) {
      console.error(
        "[Applications] load error:",
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : "Could not load applications."
      );

      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [API, getToken]);

  React.useEffect(() => {
    void loadApplications();
  }, [loadApplications]);

  const updateApplication = async (
    url: string,
    updates: {
      status?: ApplicationStatus;
      notes?: string;
    }
  ) => {
    try {
      setUpdatingUrl(url);
      setError("");

      const token = await getToken();

      if (!token) {
        setError("Please sign in first.");
        return;
      }

      const response = await fetch(
        `${API}/api/applications`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            url,
            ...updates,
          }),
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not update application (${response.status}): ${raw}`
        );
      }

      setApplications((current) =>
        current.map((item) =>
          item.url === url
            ? {
                ...item,
                ...updates,
              }
            : item
        )
      );
    } catch (err) {
      console.error(
        "[Applications] update error:",
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : "Could not update application."
      );
    } finally {
      setUpdatingUrl(null);
    }
  };

  const removeApplication = async (
    url: string
  ) => {
    try {
      setRemovingUrl(url);
      setError("");

      const token = await getToken();

      if (!token) {
        setError("Please sign in first.");
        return;
      }

      const response = await fetch(
        `${API}/api/applications`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            url,
          }),
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not remove application (${response.status}): ${raw}`
        );
      }

      setApplications((current) =>
        current.filter(
          (item) => item.url !== url
        )
      );
    } catch (err) {
      console.error(
        "[Applications] remove error:",
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : "Could not remove application."
      );
    } finally {
      setRemovingUrl(null);
    }
  };

  const formatDate = (
    value?: string | null
  ) => {
    if (!value) return "Date unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Date unavailable";
    }

    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6">
        <p className="text-sm text-gray-500">
          Loading applications...
        </p>
      </div>
    );
  }

  if (!applications.length) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Application Tracker
        </h2>

        <p className="mt-2 text-sm text-gray-500">
          No applications yet. Add a job to your
          application tracker and it will appear here.
        </p>

        {error && (
          <p className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase text-gray-400">
            Total
          </p>

          <p className="mt-1 text-2xl font-bold text-gray-900">
            {applications.length}
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase text-gray-400">
            Applied
          </p>

          <p className="mt-1 text-2xl font-bold text-gray-900">
            {
              applications.filter(
                (item) =>
                  item.status === "applied"
              ).length
            }
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase text-gray-400">
            Interview
          </p>

          <p className="mt-1 text-2xl font-bold text-gray-900">
            {
              applications.filter(
                (item) =>
                  item.status === "interview"
              ).length
            }
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase text-gray-400">
            Offers
          </p>

          <p className="mt-1 text-2xl font-bold text-gray-900">
            {
              applications.filter(
                (item) =>
                  item.status === "offer"
              ).length
            }
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {applications.map((item) => (
          <article
            key={item.id ?? item.url}
            className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.title || "Untitled role"}
                  </h3>

                  <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                    {STATUS_LABELS[item.status]}
                  </span>
                </div>

                <p className="mt-1 text-sm text-gray-500">
                  {[item.company, item.location]
                    .filter(Boolean)
                    .join(" • ")}
                </p>

                <p className="mt-2 text-xs text-gray-400">
                  Applied {formatDate(item.applied_at)}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-50"
                >
                  View Job
                </a>

                <button
                  type="button"
                  disabled={
                    removingUrl === item.url
                  }
                  onClick={() =>
                    void removeApplication(
                      item.url
                    )
                  }
                  className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                >
                  {removingUrl === item.url
                    ? "Removing..."
                    : "Remove"}
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase text-gray-400">
                  Status
                </label>

                <select
                  value={item.status}
                  disabled={
                    updatingUrl === item.url
                  }
                  onChange={(e) =>
                    void updateApplication(
                      item.url,
                      {
                        status:
                          e.target
                            .value as ApplicationStatus,
                      }
                    )
                  }
                  className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                >
                  <option value="applied">
                    Applied
                  </option>

                  <option value="interview">
                    Interview
                  </option>

                  <option value="offer">
                    Offer
                  </option>

                  <option value="rejected">
                    Rejected
                  </option>

                  <option value="withdrawn">
                    Withdrawn
                  </option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-gray-400">
                  Notes
                </label>

                <textarea
                  defaultValue={
                    item.notes ?? ""
                  }
                  placeholder="Add interview notes, follow-up details..."
                  onBlur={(e) => {
                    const next =
                      e.target.value;

                    if (
                      next !==
                      (item.notes ?? "")
                    ) {
                      void updateApplication(
                        item.url,
                        {
                          notes: next,
                        }
                      );
                    }
                  }}
                  className="mt-2 min-h-[90px] w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900"
                />
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}